import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useCurrency } from '../../context/CurrencyContext';
import useCountUp from '../../hooks/useCountUp';

const EMPTY = '—';

const normalizeDataSourceLabel = (label) => {
  if (!label) return null;
  if (typeof label === 'string') return { text: label, variant: 'default', prefix: '', connect_link: null };
  if (typeof label === 'object' && label.text) return label;
  return null;
};

const healthGlowClass = (health, isOverCommitted) => {
  if (isOverCommitted) return 'dashboard-hero-glow--tight';
  const h = (health || 'healthy').toLowerCase();
  if (h === 'tight') return 'dashboard-hero-glow--tight';
  if (h === 'critical') return 'dashboard-hero-glow--tight';
  return 'dashboard-hero-glow--healthy';
};

const SupportTile = ({ label, value }) => (
  <div className="bento-card bento-card--compact flex-1 min-w-[100px] p-4">
    <p className="text-label text-secondary mb-1">{label}</p>
    <p className="font-display text-h3 text-primary tabular-nums">{value}</p>
  </div>
);

const DashboardFinancialSnapshot = ({
  financialContext,
  dataSourceLabel,
  dashboardZero,
  onReviewVisions,
  animate = true,
}) => {
  const navigate = useNavigate();
  const { formatAmount } = useCurrency();
  const income = financialContext?.income || {};
  const health = financialContext?.financial_health || 'healthy';
  const surplusRaw = dashboardZero ? null : financialContext?.surplus_after_visions;
  const surplusNum = surplusRaw != null ? Number(surplusRaw) : null;
  const isOverCommitted = surplusNum != null && surplusNum < 0;
  const heroTarget = surplusNum != null ? Math.abs(surplusNum) : null;
  const animatedHero = useCountUp(heroTarget, 900, animate && heroTarget != null);

  const fmt = (value, empty = false) => {
    if (empty || value == null || Number.isNaN(Number(value))) return EMPTY;
    return formatAmount(value, { maximumFractionDigits: 0 });
  };

  const heroLabel = isOverCommitted ? 'Overcommitted this month' : 'Yours to use this month';
  const heroClass = isOverCommitted ? 'text-warning' : 'text-pine';
  const heroDisplay =
    heroTarget != null
      ? formatAmount(Math.round(animatedHero), { maximumFractionDigits: 0 })
      : EMPTY;

  const sourceLabel = normalizeDataSourceLabel(dataSourceLabel);

  return (
    <section
      className={`bento-card bento-card--hero dashboard-hero-glow ${healthGlowClass(health, isOverCommitted)}`}
      aria-label="Your money this month"
    >
      <p className="text-label text-secondary mb-2">{heroLabel}</p>
      <p
        className={`font-display font-bold tabular-nums leading-none ${heroClass} dashboard-hero-number`}
      >
        {heroDisplay}
      </p>
      <p className="text-label text-secondary mt-2 mb-1">
        {isOverCommitted ? 'above what your income can cover' : 'after bills and Vision commitments'}
      </p>

      {isOverCommitted && (
        <button
          type="button"
          onClick={onReviewVisions}
          className="text-body text-warning font-medium underline underline-offset-2 hover:opacity-80 mt-2"
        >
          Review your Vision commitments →
        </button>
      )}

      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-8">
        <SupportTile label="Income" value={fmt(income.monthly_net, dashboardZero)} />
        <SupportTile
          label="Committed"
          value={fmt(financialContext?.total_committed_to_visions, dashboardZero)}
        />
        <div className="col-span-2 sm:col-span-1">
          <SupportTile
            label="Variable spend"
            value={fmt(financialContext?.expenses?.variable, dashboardZero)}
          />
        </div>
      </div>

      {sourceLabel?.text && (
        <p className="text-label text-secondary mt-4 flex flex-wrap items-center gap-1">
          {sourceLabel.prefix ? `${sourceLabel.prefix} ` : ''}
          {sourceLabel.text}
          {sourceLabel.connect_link && (
            <>
              {' · '}
              <button
                type="button"
                onClick={() => navigate(sourceLabel.connect_link)}
                className="text-pine underline underline-offset-2"
              >
                Connect accounts
              </button>
            </>
          )}
        </p>
      )}

      {financialContext?.data_quality_warning && (
        <div className="mt-4 px-4 py-3 border border-warning/40 bg-warning/10 text-primary rounded-lg" role="status">
          <p className="text-body">
            Your Vision commitments exceed your income. Review your Vision amounts to build a realistic
            plan.{' '}
            <button
              type="button"
              onClick={onReviewVisions}
              className="font-medium underline underline-offset-2 text-pine"
            >
              Review Visions →
            </button>
          </p>
        </div>
      )}
    </section>
  );
};

export default DashboardFinancialSnapshot;
